// The Milanote-style board: free cards, columns and arrows on a pannable, zoomable world.
// Every change is a Yjs operation from src/shared/ops.ts with the LOCAL origin, so undo only
// reverses this person's own edits and the other members see each move live.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';
import { boardMap, cardsMap, columnSchema, parsePlacement, type Placement } from '../../shared/doc.js';
import {
  acceptFinding, addArrow, addCard, addColumn, bringToFront, columnCards, deleteArrow, effectiveTrayState, moveCards, moveColumn, placeCard, putInColumn,
  removeOrDeleteCard, resizeCard, takeOutOfColumn,
} from '../../shared/ops.js';
import { createFromText, createImages, onBoard, type Toast } from '../cards/create.js';
import { PlaceSearch } from '../map/PlaceSearch.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';
import { Arrows } from './Arrows.js';
import { CardFrame } from './CardFrame.js';
import { Columns } from './Columns.js';
import { ESTIMATED_CARD_HEIGHT, fitView, intersects, normRect, screenToWorld, visibleRect, zoomAt, type Rect, type View } from './geometry.js';
import { Cursors, type PeerState } from './Presence.js';

export const CARD_DRAG_TYPE = 'application/x-plana-card';
const DRAG_THRESHOLD = 4;

type Gesture =
  | { kind: 'pan'; sx: number; sy: number; start: View }
  | { kind: 'marquee'; x1: number; y1: number; additive: boolean; base: Set<string> }
  | { kind: 'cards'; sx: number; sy: number; ids: string[]; origins: Map<string, { x: number; y: number }>; moved: boolean; fromColumn: string | null }
  | { kind: 'resize'; sx: number; cardId: string; w: number }
  | { kind: 'column'; sx: number; sy: number; columnId: string; start: { x: number; y: number } };

function loadView(boardId: string): View | null {
  try {
    return JSON.parse(sessionStorage.getItem(`plana-view-${boardId}`) ?? 'null');
  } catch {
    return null;
  }
}

export function Canvas({ conn, projectId, boardId, peers, onOpenCard, toast }: {
  conn: ProjectConn;
  projectId: string;
  boardId: string;
  peers: PeerState[];
  onOpenCard: (cardId: string) => void;
  toast: Toast;
}) {
  const board = boardMap(conn.doc, boardId) as Y.Map<any> | undefined;
  const version = useYVersion(board);
  const containerRef = useRef<HTMLDivElement>(null);
  const [world, setWorld] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 1200, h: 800 });
  const [view, setView] = useState<View>(() => loadView(boardId) ?? { ox: 80, oy: 80, zoom: 1 });
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [selectedArrow, setSelectedArrow] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [marquee, setMarquee] = useState<Rect | null>(null);
  const [arrowFrom, setArrowFrom] = useState<string | null | false>(false); // false: arrow mode off
  const [placing, setPlacing] = useState(false); // the "+ Place" search is open
  const gesture = useRef<Gesture | null>(null);
  const heights = useRef(new Map<string, number>());
  const space = useRef(false);
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => sessionStorage.setItem(`plana-view-${boardId}`, JSON.stringify(view)), [boardId, view]);

  useEffect(() => {
    const el = containerRef.current!;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Free placements, parsed safely: unknown/malformed entries are skipped rather than crashing
  // the board (Review Focus #5), and a placement whose columnId points at a column that no
  // longer exists, or that doesn't (any more) list this card, is drawn as free rather than
  // disappearing (e.g. Alice drops a card into column C while Bob deletes C).
  const placements = useMemo(() => {
    const cols = board?.get('columns') as Y.Map<Y.Map<any>> | undefined;
    const inColumn = new Map<string, Set<string>>();
    // Only columns that pass columnSchema count here, matching Columns.tsx's own rendering
    // decision: a column that isn't a well-shaped Y.Map (future version, buggy agent) is skipped
    // rather than crashing, and its cards are shown as free rather than silently hidden.
    cols?.forEach((col, colId) => {
      if (col instanceof Y.Map && columnSchema.safeParse(col.toJSON()).success) {
        inColumn.set(colId, new Set(columnCards(conn.doc, boardId, colId)));
      }
    });
    const out: [string, Placement][] = [];
    (board?.get('placements') as Y.Map<unknown> | undefined)?.forEach((p, id) => {
      if (!(p instanceof Y.Map)) return;
      const parsed = parsePlacement(p.toJSON());
      if (!parsed) return;
      const shownInColumn = parsed.columnId ? (inColumn.get(parsed.columnId)?.has(id) ?? false) : false;
      if (!shownInColumn) out.push([id, parsed]);
    });
    return out;
  }, [board, version, conn.doc, boardId]);

  const rectOf = useCallback((id: string, p: Placement): Rect => ({ x: p.pos.x, y: p.pos.y, w: p.size.w, h: p.size.h ?? heights.current.get(id) ?? ESTIMATED_CARD_HEIGHT }), []);
  const visible = visibleRect(view, size.w, size.h);
  // CardFrame measures el.offsetHeight, the element's own layout (border-box) height: the
  // .world ancestor's CSS `scale(zoom)` transform is purely visual and doesn't affect layout,
  // so that height is already in world units — dividing by zoom again shrank it at any zoom≠1,
  // breaking culling, the marquee and Fit.
  const onMeasure = useCallback((id: string, h: number) => heights.current.set(id, h), []);

  const toWorld = (clientX: number, clientY: number) => {
    const r = containerRef.current!.getBoundingClientRect();
    return screenToWorld(viewRef.current, clientX - r.left, clientY - r.top);
  };
  const centre = () => screenToWorld(viewRef.current, size.w / 2 - 130, size.h / 2 - 80);

  const movedBy = useCallback(
    (cardId: string) => {
      const p = peers.find((x) => x.dragging?.boardId === boardId && x.dragging.cardIds.includes(cardId));
      return p?.user ? { name: p.user.name, colour: p.user.colour } : null;
    },
    [peers, boardId],
  );

  // --- Pointer gestures (window listeners so a card can leave its column mid-drag) ---------------

  const frame = useRef<number | null>(null);
  const pendingMoves = useRef<{ cardId: string; pos: { x: number; y: number } }[] | null>(null);
  const flushMoves = () => {
    frame.current = null;
    if (pendingMoves.current) moveCards(conn.doc, boardId, pendingMoves.current);
    pendingMoves.current = null;
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const g = gesture.current;
      const awareness = conn.provider.awareness;
      const w = toWorld(e.clientX, e.clientY);
      awareness?.setLocalStateField('pointer', { boardId, x: Math.round(w.x), y: Math.round(w.y) });
      if (!g) return;
      if (g.kind === 'pan') {
        setView({ ...g.start, ox: g.start.ox + e.clientX - g.sx, oy: g.start.oy + e.clientY - g.sy });
      } else if (g.kind === 'marquee') {
        const rect = normRect(g.x1, g.y1, w.x, w.y);
        setMarquee(rect);
        const hit = new Set(g.base);
        for (const [id, p] of placements) if (intersects(rect, rectOf(id, p))) hit.add(id);
        setSelection(hit);
      } else if (g.kind === 'cards') {
        const dx = (e.clientX - g.sx) / viewRef.current.zoom;
        const dy = (e.clientY - g.sy) / viewRef.current.zoom;
        if (!g.moved && Math.hypot(e.clientX - g.sx, e.clientY - g.sy) < DRAG_THRESHOLD) return;
        if (!g.moved) {
          g.moved = true;
          conn.undo.stopCapturing();
          if (g.fromColumn) takeOutOfColumn(conn.doc, boardId, g.ids[0], g.origins.get(g.ids[0])!);
          bringToFront(conn.doc, boardId, g.ids);
          awareness?.setLocalStateField('dragging', { boardId, cardIds: g.ids });
        }
        pendingMoves.current = g.ids.map((id) => ({ cardId: id, pos: { x: g.origins.get(id)!.x + dx, y: g.origins.get(id)!.y + dy } }));
        frame.current ??= requestAnimationFrame(flushMoves);
      } else if (g.kind === 'resize') {
        const w2 = g.w + (e.clientX - g.sx) / viewRef.current.zoom;
        resizeCard(conn.doc, boardId, g.cardId, { w: w2, h: null });
      } else if (g.kind === 'column') {
        moveColumn(conn.doc, boardId, g.columnId, { x: g.start.x + (e.clientX - g.sx) / viewRef.current.zoom, y: g.start.y + (e.clientY - g.sy) / viewRef.current.zoom });
      }
    };
    const onUp = (e: PointerEvent) => {
      const g = gesture.current;
      gesture.current = null;
      setMarquee(null);
      if (g?.kind === 'cards') {
        if (frame.current) cancelAnimationFrame(frame.current);
        flushMoves();
        conn.provider.awareness?.setLocalStateField('dragging', null);
        if (g.moved && g.ids.length === 1) dropIntoColumn(g.ids[0], e.clientX, e.clientY);
        conn.undo.stopCapturing();
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp); // a cancelled gesture must still finish like a pointerup, not get stuck
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // This effect re-runs on every move during a drag (placements changes as the card moves),
    // which is harmless here: onMove/onUp are freshly bound but read the same gesture/frame
    // refs, so an in-progress drag survives the swap. (A real teardown — unmount, or boardId
    // changing, which remounts Canvas since the parent keys it by boardId — is handled by the
    // mount-only effect below, which doesn't re-run mid-drag and so can safely clear `dragging`.)
  }, [conn, boardId, placements]);

  // Mount-only: if this component is destroyed mid-drag (switching boards, or away from the
  // project), clear this user's `dragging` awareness field so peers don't keep seeing a stuck
  // "moved by" tag on a card nobody is dragging any more.
  const connRef = useRef(conn);
  connRef.current = conn;
  useEffect(() => {
    return () => {
      if (gesture.current?.kind === 'cards') connRef.current.provider.awareness?.setLocalStateField('dragging', null);
    };
  }, []);

  function dropIntoColumn(cardId: string, clientX: number, clientY: number) {
    const colEl = document.elementsFromPoint(clientX, clientY).map((el) => el.closest<HTMLElement>('[data-column-id]')).find(Boolean);
    if (!colEl) return;
    const others = [...colEl.querySelectorAll<HTMLElement>('.column-cards > [data-card-id]')].filter((el) => el.dataset.cardId !== cardId);
    const index = others.filter((el) => {
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2 < clientY;
    }).length;
    putInColumn(conn.doc, boardId, cardId, colEl.dataset.columnId!, index);
  }

  const onBackgroundPointerDown = (e: React.PointerEvent) => {
    if (e.target !== containerRef.current && e.target !== world && !(e.target as Element).classList?.contains('arrows')) return;
    setEditingId(null);
    setSelectedArrow(null);
    if (space.current || e.button === 1) {
      gesture.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, start: viewRef.current };
      return;
    }
    if (e.button !== 0) return;
    const w = toWorld(e.clientX, e.clientY);
    const base = e.shiftKey ? new Set(selection) : new Set<string>();
    if (!e.shiftKey) setSelection(new Set());
    gesture.current = { kind: 'marquee', x1: w.x, y1: w.y, additive: e.shiftKey, base };
  };

  const onCardPointerDown = useCallback(
    (e: React.PointerEvent, cardId: string) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      setSelectedArrow(null);
      if (arrowFrom !== false) {
        if (arrowFrom === null) setArrowFrom(cardId);
        else {
          addArrow(conn.doc, boardId, arrowFrom, cardId);
          setArrowFrom(false);
        }
        return;
      }
      let ids: string[];
      if (e.shiftKey) {
        const next = new Set(selection);
        if (next.has(cardId)) next.delete(cardId);
        else next.add(cardId);
        setSelection(next);
        return;
      } else if (selection.has(cardId)) ids = [...selection];
      else {
        ids = [cardId];
        setSelection(new Set([cardId]));
      }
      const placementsMap = board?.get('placements') as Y.Map<Y.Map<any>>;
      const fromColumn = placementsMap.get(cardId)?.get('columnId') ?? null;
      if (fromColumn) {
        // A card in a column has no free position yet: start from where it is drawn.
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        ids = [cardId];
        const at = toWorld(r.left, r.top);
        gesture.current = { kind: 'cards', sx: e.clientX, sy: e.clientY, ids, origins: new Map([[cardId, at]]), moved: false, fromColumn };
        return;
      }
      const origins = new Map(ids.map((id) => [id, placementsMap.get(id)?.get('pos') as { x: number; y: number }]).filter(([, p]) => p) as [string, { x: number; y: number }][]);
      gesture.current = { kind: 'cards', sx: e.clientX, sy: e.clientY, ids: [...origins.keys()], origins, moved: false, fromColumn: null };
    },
    [arrowFrom, selection, board, conn, boardId],
  );

  const onResizeStart = useCallback(
    (e: React.PointerEvent, cardId: string) => {
      e.stopPropagation();
      const p = (board?.get('placements') as Y.Map<Y.Map<any>>).get(cardId);
      conn.undo.stopCapturing();
      gesture.current = { kind: 'resize', sx: e.clientX, cardId, w: (p?.get('size') as { w: number }).w };
    },
    [board, conn],
  );

  const onColumnPointerDown = useCallback(
    (e: React.PointerEvent, columnId: string) => {
      e.stopPropagation();
      const col = (board?.get('columns') as Y.Map<Y.Map<any>>).get(columnId);
      conn.undo.stopCapturing();
      gesture.current = { kind: 'column', sx: e.clientX, sy: e.clientY, columnId, start: col?.get('pos') };
    },
    [board, conn],
  );

  // --- Wheel (pan / ctrl-zoom), keyboard, paste ---------------------------------------------------

  useEffect(() => {
    const el = containerRef.current!;
    const onWheel = (e: WheelEvent) => {
      if ((e.target as HTMLElement).closest('.card-frame.editing')) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) setView((v) => zoomAt(v, e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY / 300)));
      else setView((v) => ({ ...v, ox: v.ox - e.deltaX, oy: v.oy - e.deltaY }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !typing(e.target)) {
        space.current = e.type === 'keydown';
        if (e.type === 'keydown') e.preventDefault();
        return;
      }
      if (e.type !== 'keydown' || typing(e.target) || document.querySelector('.card-panel')) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) conn.undo.redo();
        else conn.undo.undo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedArrow) deleteArrow(conn.doc, boardId, selectedArrow);
        for (const id of selection) removeOrDeleteCard(conn.doc, conn.me, boardId, id);
        setSelection(new Set());
        setSelectedArrow(null);
      } else if (e.key === 'Escape') {
        setSelection(new Set());
        setArrowFrom(false);
      } else if (e.key === 'Enter' && selection.size === 1) {
        onOpenCard([...selection][0]);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
    };
  }, [conn, boardId, selection, selectedArrow, onOpenCard]);

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (typing(e.target) || document.querySelector('.card-panel')) return;
      const files = [...(e.clipboardData?.files ?? [])].filter((f) => f.type.startsWith('image/'));
      const at = centre();
      e.preventDefault();
      if (files.length) void createImages(conn, projectId, files, (i) => onBoard(boardId, { x: at.x + i * 30, y: at.y + i * 30 }), toast);
      else {
        const id = createFromText(conn, projectId, onBoard(boardId, at), e.clipboardData?.getData('text/plain') ?? '');
        if (id) setSelection(new Set([id]));
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [conn, projectId, boardId, size]);

  // --- Drop: Spark cards, files, links -----------------------------------------------------------

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const at = toWorld(e.clientX, e.clientY);
    // A card dragged from Spark or the Suggested tray. A suggested finding dropped here is accepted right where it landed.
    const draggedId = e.dataTransfer.getData(CARD_DRAG_TYPE);
    if (draggedId) {
      if (cardsMap(conn.doc).get(draggedId)?.get('kind') === 'finding' && effectiveTrayState(conn.doc, draggedId) !== 'accepted') {
        return acceptFinding(conn.doc, conn.me, draggedId, { boardId, pos: at });
      }
      return placeCard(conn.doc, boardId, draggedId, at);
    }
    const files = [...e.dataTransfer.files];
    if (files.length) {
      const images = files.filter((f) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|heic)$/i.test(f.name));
      for (const f of files.filter((x) => !images.includes(x))) toast(`${f.name}: Only JPEG, PNG and WebP images can be added.`);
      void createImages(conn, projectId, images, (i) => onBoard(boardId, { x: at.x + i * 30, y: at.y + i * 30 }), toast);
      return;
    }
    createFromText(conn, projectId, onBoard(boardId, at), e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain'));
  };

  // --- Toolbar ------------------------------------------------------------------------------------

  const add = (kind: 'note' | 'todo' | 'palette') => {
    const at = centre();
    const input = kind === 'note' ? { kind, text: '' } : kind === 'todo' ? { kind, title: '' } : { kind, colours: [{ hex: '#1f2937' }, { hex: '#d97706' }, { hex: '#e5e7eb' }] };
    const id = addCard(conn.doc, conn.me, input as never, onBoard(boardId, at));
    setSelection(new Set([id]));
    setEditingId(id);
  };
  const fileInput = useRef<HTMLInputElement>(null);
  const placeButton = useRef<HTMLButtonElement>(null);
  const fit = () => setView(fitView(placements.map(([id, p]) => rectOf(id, p)), size.w, size.h));
  const closePlacing = () => {
    setPlacing(false);
    placeButton.current?.focus();
  };

  if (!board) return <p className="error">This board doesn't exist.</p>;

  return (
    <div className="canvas-wrap">
      <div className="toolbar" role="toolbar" aria-label="Board tools">
        <button onClick={() => add('note')}>+ Note</button>
        <button onClick={() => add('todo')}>+ To-do</button>
        <button onClick={() => add('palette')}>+ Palette</button>
        <button onClick={() => fileInput.current?.click()}>+ Image</button>
        <button ref={placeButton} aria-expanded={placing} onClick={() => setPlacing((v) => !v)}>+ Place</button>
        <button onClick={() => addColumn(conn.doc, boardId, 'New column', centre())}>+ Column</button>
        <button aria-pressed={arrowFrom !== false} onClick={() => setArrowFrom(arrowFrom === false ? null : false)}>
          {arrowFrom === false ? '→ Arrow' : arrowFrom === null ? 'Pick start card…' : 'Pick end card…'}
        </button>
        <span className="spacer" />
        <button onClick={() => conn.undo.undo()} aria-label="Undo">↶</button>
        <button onClick={() => conn.undo.redo()} aria-label="Redo">↷</button>
        <button onClick={() => setView((v) => zoomAt(v, size.w / 2, size.h / 2, 1 / 1.2))} aria-label="Zoom out">−</button>
        <span className="zoom">{Math.round(view.zoom * 100)}%</span>
        <button onClick={() => setView((v) => zoomAt(v, size.w / 2, size.h / 2, 1.2))} aria-label="Zoom in">+</button>
        <button onClick={fit}>Fit</button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          data-testid="image-input"
          onChange={(e) => {
            const at = centre();
            void createImages(conn, projectId, [...(e.target.files ?? [])], (i) => onBoard(boardId, { x: at.x + i * 30, y: at.y + i * 30 }), toast);
            e.target.value = '';
          }}
        />
      </div>
      {placing && (
        <div
          className="place-popover"
          role="dialog"
          aria-label="Add a place"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation(); // don't also let the canvas's own Escape handler clear its selection
              closePlacing();
            }
          }}
        >
          <PlaceSearch
            projectId={projectId}
            autoFocus
            onPick={(r) => {
              conn.undo.stopCapturing(); // its own undo step (controller ruling D11)
              const id = addCard(conn.doc, conn.me, { kind: 'place', name: r.name || r.displayName.split(',')[0], lat: r.lat, lon: r.lon, address: r.displayName }, onBoard(boardId, centre()));
              setSelection(new Set([id]));
              setPlacing(false);
            }}
          />
          <button className="link small" onClick={closePlacing}>Close</button>
        </div>
      )}
      <div
        ref={containerRef}
        className={`canvas${arrowFrom !== false ? ' arrow-mode' : ''}`}
        data-board-id={boardId}
        tabIndex={0}
        onPointerDown={onBackgroundPointerDown}
        onPointerLeave={() => conn.provider.awareness?.setLocalStateField('pointer', null)}
        onDoubleClick={(e) => {
          if (e.target !== containerRef.current && e.target !== world) return;
          const id = addCard(conn.doc, conn.me, { kind: 'note', text: '' }, onBoard(boardId, toWorld(e.clientX, e.clientY)));
          setSelection(new Set([id]));
          setEditingId(id);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        <div ref={setWorld} className="world" style={{ transform: `translate(${view.ox}px, ${view.oy}px) scale(${view.zoom})` }}>
          <Columns
            conn={conn}
            projectId={projectId}
            boardId={boardId}
            selection={selection}
            editingId={editingId}
            movedBy={movedBy}
            onPointerDown={onCardPointerDown}
            onEdit={setEditingId}
            onOpen={onOpenCard}
            onResizeStart={onResizeStart}
            onMeasure={onMeasure}
            onColumnPointerDown={onColumnPointerDown}
          />
          {placements
            .filter(([id, p]) => selection.has(id) || editingId === id || intersects(visible, rectOf(id, p)))
            .map(([id, p]) => (
              <CardFrame
                key={id}
                conn={conn}
                projectId={projectId}
                cardId={id}
                placement={p}
                selected={selection.has(id)}
                editing={editingId === id}
                movedBy={movedBy(id)}
                onPointerDown={onCardPointerDown}
                onEdit={setEditingId}
                onOpen={onOpenCard}
                onResizeStart={onResizeStart}
                onMeasure={onMeasure}
              />
            ))}
          <Arrows conn={conn} boardId={boardId} view={view} world={world} version={version} selected={selectedArrow} onSelect={setSelectedArrow} />
          {marquee && <div className="marquee" style={{ left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h }} />}
          <Cursors peers={peers} boardId={boardId} zoom={view.zoom} />
        </div>
        {placements.length === 0 && <p className="canvas-hint">Double-click to add a note, paste a link or drop images here.</p>}
      </div>
    </div>
  );
}
